import * as React from 'react';

import { onUnauthorized, registerCsrfRefresher } from '@/lib/api/client';
import { clearCsrfToken, setCsrfToken } from '@/lib/api/csrf';
import { getMe, signout as apiSignout } from '@/lib/api/endpoints';
import { isApiError } from '@/lib/api/error';
import type { SessionPayload, UserRead } from '@/types';

export type UserAuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

export interface UserAuthState {
  status: UserAuthStatus;
  user: UserRead | null;
  /**
   * The most recently issued recovery code, if any. Surfaced once at
   * signup and after a recovery-code consume; cleared as soon as the
   * UI acknowledges it (the welcome modal calls `clearRecoveryCode`).
   * Never persisted to storage.
   */
  recoveryCode: string | null;
  /** Apply a freshly-minted session (post-consume) to the context. */
  applySession: (payload: SessionPayload) => void;
  /** Clear the recovery code from memory after the user has seen it. */
  clearRecoveryCode: () => void;
  /** Hit `/auth/me` and refresh the context from server state. */
  refresh: () => Promise<void>;
  signout: () => Promise<void>;
}

const UserAuthContext = React.createContext<UserAuthState | null>(null);

export function UserAuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [status, setStatus] = React.useState<UserAuthStatus>('loading');
  const [user, setUser] = React.useState<UserRead | null>(null);
  const [recoveryCode, setRecoveryCode] = React.useState<string | null>(null);

  // Monotonic counter bumped on every *authoritative* transition
  // (`applySession`, `signout`). An async `/auth/me` read captures the
  // generation it started in and discards its result if a newer transition
  // landed meanwhile — so a slow mount-time refresh can't clobber the session
  // a just-completed magic-link consume installed. This is the race behind
  // the "saving a stop logged me out" report right after signup.
  const generation = React.useRef(0);
  // Mirror of `status` readable synchronously from the 401 listener without
  // re-subscribing on every status change.
  const statusRef = React.useRef<UserAuthStatus>(status);
  React.useEffect(() => {
    statusRef.current = status;
  }, [status]);
  // Guards against firing overlapping confirmation probes when several
  // requests 401 at once (e.g. the list + write a single save-stop makes).
  const confirming = React.useRef(false);

  const refresh = React.useCallback(async () => {
    const gen = generation.current;
    try {
      const me = await getMe();
      if (gen !== generation.current) return;
      if (me.authenticated && me.user) {
        // The backend rotates and returns a fresh CSRF token on each
        // `/auth/me` call so a hard reload (which wipes our in-memory
        // store) can rehydrate without forcing a re-sign-in. The old
        // token stops working; multi-tab users may see a transient 403
        // on writes from the tab that hasn't refreshed yet.
        if (me.csrf_token) {
          setCsrfToken(me.csrf_token, 'user');
        }
        setStatus('authenticated');
        setUser(me.user);
      } else {
        clearCsrfToken('user');
        setStatus('unauthenticated');
        setUser(null);
      }
    } catch {
      if (gen !== generation.current) return;
      clearCsrfToken('user');
      setStatus('unauthenticated');
      setUser(null);
    }
  }, []);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  // Self-heal a rejected user CSRF token: when a write 403s, the shared
  // client calls this to re-fetch `/auth/me` (which mints a fresh token) and
  // retries. Single-flight so a burst of writes 403-ing at once triggers one
  // refresh, not a thundering herd of `/me` calls that would re-race the
  // server's rotation. Unlike `refresh`, it only touches the token — never
  // the auth status — so a transient blip can't flip the user to signed-out.
  const csrfRefreshInFlight = React.useRef<Promise<void> | null>(null);
  React.useEffect(() => {
    const refreshCsrf = async (): Promise<void> => {
      if (csrfRefreshInFlight.current) {
        return csrfRefreshInFlight.current;
      }
      const run = (async () => {
        try {
          const me = await getMe();
          if (me.authenticated && me.user) {
            if (me.csrf_token) {
              setCsrfToken(me.csrf_token, 'user');
            }
          } else {
            clearCsrfToken('user');
          }
        } catch {
          // Keep the session; the write's original 403 will surface.
        } finally {
          csrfRefreshInFlight.current = null;
        }
      })();
      csrfRefreshInFlight.current = run;
      return run;
    };
    return registerCsrfRefresher('user', refreshCsrf);
  }, []);

  React.useEffect(() => {
    // The shared 401 channel covers both admin and user cookies. A single
    // 401 is *not* proof the session ended: right after signup the first
    // authenticated write can 401 on a transient cookie/CSRF mismatch while
    // the session is in fact valid. So we treat the signal as "go verify",
    // not "log out" — confirm against `/auth/me` (which returns 200 with
    // `authenticated:false` on a real logout, never a 401, so it can't
    // recurse) and only tear the session down when it actually reports gone.
    return onUnauthorized(() => {
      if (statusRef.current !== 'authenticated') return;
      if (confirming.current) return;
      confirming.current = true;
      const gen = generation.current;
      void (async () => {
        try {
          const me = await getMe();
          if (gen !== generation.current) return;
          if (me.authenticated && me.user) {
            if (me.csrf_token) {
              setCsrfToken(me.csrf_token, 'user');
            }
          } else {
            clearCsrfToken('user');
            setStatus('unauthenticated');
            setUser(null);
          }
        } catch {
          // Network/5xx while probing: keep the session. A genuine loss
          // resurfaces on the next read or write rather than logging the
          // user out on a transient blip.
        } finally {
          confirming.current = false;
        }
      })();
    });
  }, []);

  const applySession = React.useCallback((payload: SessionPayload) => {
    generation.current += 1;
    if (payload.csrf_token) {
      setCsrfToken(payload.csrf_token, 'user');
    }
    if (payload.authenticated && payload.user) {
      setStatus('authenticated');
      setUser(payload.user);
    } else {
      setStatus('unauthenticated');
      setUser(null);
    }
    if (payload.recovery_code) {
      setRecoveryCode(payload.recovery_code);
    }
  }, []);

  const clearRecoveryCode = React.useCallback(() => {
    setRecoveryCode(null);
  }, []);

  const signout = React.useCallback(async () => {
    generation.current += 1;
    try {
      await apiSignout();
    } catch (err) {
      if (!isApiError(err) || err.status !== 401) {
        throw err;
      }
    } finally {
      clearCsrfToken('user');
      setStatus('unauthenticated');
      setUser(null);
      setRecoveryCode(null);
    }
  }, []);

  const value = React.useMemo<UserAuthState>(
    () => ({
      status,
      user,
      recoveryCode,
      applySession,
      clearRecoveryCode,
      refresh,
      signout,
    }),
    [status, user, recoveryCode, applySession, clearRecoveryCode, refresh, signout],
  );

  return <UserAuthContext.Provider value={value}>{children}</UserAuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserAuth(): UserAuthState {
  const ctx = React.useContext(UserAuthContext);
  if (!ctx) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return ctx;
}

/**
 * Non-throwing variant: returns `null` when no `UserAuthProvider` is in
 * the tree instead of throwing. For surfaces that *optionally* layer in
 * a signed-in affordance (e.g. "alerts on my saved stops") but must also
 * render standalone — including in tests that don't mount the provider.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalUserAuth(): UserAuthState | null {
  return React.useContext(UserAuthContext);
}
